/**
 * 試す（dry run）— 版を入れ替える前に、何がどう変わるかを見比べる段。
 *
 * この面が守っていること:
 * - **答えを 1 件も書かない。** 結果は台帳の比較表にだけ残る。画面もそう断る
 * - 非同期（ジョブ）なので、起動の応答に入っていた状態を持ち続けず、
 *   `dryRun(jobId:)` を引き直す。間隔は `Job.suggestedPollIntervalSec` に従う
 * - **0 件のときに理由を言う。** 材料から確かな問いが作れなければ作らないのが
 *   設計どおりの振る舞いで、黙って空表を出すと「壊れている」と読まれる
 * - 試したのは名指しした商品ぶんだけで、ルール全体の生成件数ではないと添える
 */

import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  DryRunDocument,
  DryRunRuleDocument,
  ErrorState,
  LoadingState,
  Meter,
  Tag,
  TenantRunningJobsDocument,
  type DryRunFieldsFragment,
  type JobState,
  type RuleDetailFieldsFragment,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

import {
  DRY_RUN_DEFAULT_SAMPLE_SIZE,
  DRY_RUN_MAX_SAMPLE_SIZE,
  TENANT_WIDE_JOB_NOTE,
} from "../api-capabilities";
import { DroppedReasons, RuleErrors, type RuleUserError } from "./RuleParts";

/** 終端。ここに来たらポーリングを止める */
const TERMINAL_STATES: readonly JobState[] = [
  "SUCCEEDED",
  "SUCCEEDED_WITH_ERRORS",
  "FAILED",
  "CANCELLED",
];

function isTerminal(state: JobState): boolean {
  return TERMINAL_STATES.includes(state);
}

function count(value: number | null | undefined): string {
  return value == null ? "—" : value.toLocaleString("ja-JP");
}

/** 「作らなかった」と「空の答えを作った」は別物なので、空文字で埋めない */
function answerCell(body: string | null, note: string | null) {
  if (body === null) {
    return <span className="vd-stat-note">この版は作りませんでした</span>;
  }
  return (
    <>
      <div>{body}</div>
      {note === null ? null : <div className="vd-stat-note">根拠: {note}</div>}
    </>
  );
}

function ComparisonTable({ result }: { result: DryRunFieldsFragment }) {
  const comparison = result.comparison;
  if (comparison === null) {
    return null;
  }
  return (
    <>
      <div className="vd-grid">
        <div>
          <div className="vd-stat-label">いまの答え</div>
          <div className="vd-stat-value">
            {count(comparison.generatedBefore)} 件
          </div>
          <div className="vd-stat-note">試した商品ぶんだけの数です</div>
        </div>
        <div>
          <div className="vd-stat-label">試した版の答え</div>
          <div className="vd-stat-value">
            {count(comparison.generatedAfter)} 件
          </div>
          <div className="vd-stat-note">保存はしていません</div>
        </div>
        <div>
          <div className="vd-stat-label">禁止表現に当たる（いま）</div>
          <div className="vd-stat-value">
            {count(comparison.bannedHitsBefore)} 件
          </div>
          <div className="vd-stat-note">
            いまの答えを、試した版の禁止表現で数え直したものです
          </div>
        </div>
        <div>
          <div className="vd-stat-label">禁止表現で捨てた（試した版）</div>
          <div className="vd-stat-value">
            {count(comparison.bannedHitsAfter)} 件
          </div>
        </div>
      </div>

      {comparison.answers.length === 0 ? (
        <div className="vd-notice" role="status" style={{ marginTop: 12 }}>
          <p style={{ margin: 0 }}>
            見比べる答えがありませんでした。試した版も、いま保存されている答えも
            0 件です。
          </p>
          <DroppedReasons
            droppedFaqs={result.job.droppedFaqs}
            reasons={result.job.droppedFaqReasons}
          />
          {result.job.droppedFaqReasons.length === 0 ? (
            <p className="vd-stat-note" style={{ margin: "8px 0 0" }}>
              捨てた答えはありません。材料から根拠を示して答えられる問いが
              無かった、ということです（失敗ではありません）。
            </p>
          ) : null}
        </div>
      ) : (
        <div className="vd-table-scroll" style={{ marginTop: 12 }}>
          <table className="vd-table">
            <thead>
              <tr>
                <th scope="col">商品</th>
                <th scope="col">問い</th>
                <th scope="col">いまの答え</th>
                <th scope="col">試した版の答え</th>
                <th scope="col">変わるか</th>
              </tr>
            </thead>
            <tbody>
              {comparison.answers.map((answer) => (
                <tr key={`${answer.productId}-${answer.question}`}>
                  <td>{answer.productId}</td>
                  <td>{answer.question}</td>
                  <td>{answerCell(answer.before, answer.beforeNote)}</td>
                  <td>{answerCell(answer.after, answer.afterNote)}</td>
                  <td>
                    {answer.changed ? (
                      <Tag tone="warn">変わります</Tag>
                    ) : (
                      <Tag>同じ</Tag>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DroppedReasons
        droppedFaqs={result.job.droppedFaqs}
        reasons={
          comparison.answers.length === 0 ? [] : result.job.droppedFaqReasons
        }
      />
    </>
  );
}

export function RuleDryRunPanel({ rule }: { rule: RuleDetailFieldsFragment }) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [pollTick, setPollTick] = useState(0);
  const [sampleSize, setSampleSize] = useState(DRY_RUN_DEFAULT_SAMPLE_SIZE);
  const [productIds, setProductIds] = useState("");
  const [promptVersion, setPromptVersion] = useState("");
  const [userErrors, setUserErrors] = useState<RuleUserError[]>([]);

  const [startState, dryRunRule] = useMutation(DryRunRuleDocument);
  const [result, refetchResult] = useQuery({
    query: DryRunDocument,
    variables: { jobId: jobId ?? "" },
    pause: jobId === null,
    requestPolicy: "network-only" as const,
  });

  // 画面を開き直したときに拾い直すぶん。**テナント全体**でしか引けない
  const [runningResult] = useQuery({
    query: TenantRunningJobsDocument,
    variables: { kind: "RULE_DRY_RUN" as const, state: "RUNNING" as JobState },
    pause: jobId !== null,
  });
  const [queuedResult] = useQuery({
    query: TenantRunningJobsDocument,
    variables: { kind: "RULE_DRY_RUN" as const, state: "QUEUED" as JobState },
    pause: jobId !== null,
  });
  // 拾い直したぶんかどうか。**このルールのぶんとは言い切れない**ので区別する
  const [pickedUp, setPickedUp] = useState(false);

  const dryRun = result.data?.dryRun ?? null;

  useEffect(() => {
    if (jobId !== null) {
      return;
    }
    const found =
      runningResult.data?.jobs.edges[0]?.node ??
      queuedResult.data?.jobs.edges[0]?.node ??
      null;
    if (found !== null) {
      setJobId(found.id);
      setPickedUp(true);
    }
  }, [jobId, runningResult.data, queuedResult.data]);

  // 次に引くまでの秒数はサーバーが指示する。null なら終端なので止める
  useEffect(() => {
    if (jobId === null || dryRun === null) {
      return;
    }
    const intervalSec = dryRun.job.suggestedPollIntervalSec;
    if (intervalSec == null || intervalSec <= 0) {
      return;
    }
    const timer = setTimeout(() => {
      setPollTick((tick) => tick + 1);
      refetchResult({ requestPolicy: "network-only" });
    }, intervalSec * 1000);
    return () => clearTimeout(timer);
  }, [jobId, dryRun, pollTick, refetchResult]);

  const start = useCallback(() => {
    const named = productIds
      .split(/[\s,]+/)
      .map((value) => value.trim())
      .filter((value) => value !== "");
    void dryRunRule({
      input: {
        ruleId: rule.id,
        ...(promptVersion === "" ? {} : { promptVersion }),
        ...(named.length === 0 ? { sampleSize } : { productIds: named }),
      },
    }).then((mutationResult) => {
      if (mutationResult.error !== undefined) {
        setUserErrors([
          {
            field: null,
            code: "INTERNAL_ERROR",
            message: `試せませんでした: ${mutationResult.error.message}`,
          },
        ]);
        return;
      }
      const payload = mutationResult.data?.dryRunRule;
      if (payload === undefined) {
        return;
      }
      if (payload.userErrors.length > 0) {
        setUserErrors([...payload.userErrors]);
        return;
      }
      setUserErrors([]);
      const started = payload.result?.job ?? null;
      if (started !== null) {
        // 起動の応答の状態は持ち続けない。id だけ受け取って台帳から引き直す
        setJobId(started.id);
        setPickedUp(false);
      }
    });
  }, [dryRunRule, productIds, promptVersion, rule.id, sampleSize]);

  const versions = rule.promptVersions.edges.map((edge) => edge.node);

  return (
    <Card
      title="試して見比べる"
      extra={<HelpIcon slug="rules/dry-run" />}
      note="試した答えは保存しません。配信もされません。AI を呼ぶので、試した回数だけ月の予算を使います。"
    >
      <div className="vd-stack">
        <RuleErrors title="試せませんでした" errors={userErrors} />

        {jobId === null ? (
          <>
            <div className="vd-field">
              <label htmlFor="dry-run-version">試す版</label>
              <span className="vd-field-hint">
                選ばないと下書きを試します（下書きが無ければ配信中の版）
              </span>
              <select
                id="dry-run-version"
                className="vd-input"
                value={promptVersion}
                onChange={(event) => setPromptVersion(event.target.value)}
              >
                <option value="">下書き</option>
                {versions.map((version) => (
                  <option
                    key={version.promptVersion}
                    value={version.promptVersion}
                  >
                    {version.promptVersion}
                    {version.isPublished ? "（配信中）" : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="vd-field">
              <label htmlFor="dry-run-products">試す商品</label>
              <span className="vd-field-hint">
                商品 ID を空白か改行で区切ります。空欄なら母数の先頭から試します
              </span>
              <textarea
                id="dry-run-products"
                className="vd-input"
                rows={2}
                value={productIds}
                onChange={(event) => setProductIds(event.target.value)}
              />
            </div>

            <div className="vd-field">
              <label htmlFor="dry-run-sample">
                商品を名指ししないときの件数
              </label>
              <span className="vd-field-hint">
                費用が出るので {DRY_RUN_MAX_SAMPLE_SIZE} 件までです
              </span>
              <input
                id="dry-run-sample"
                className="vd-input"
                type="number"
                min={1}
                max={DRY_RUN_MAX_SAMPLE_SIZE}
                value={sampleSize}
                onChange={(event) =>
                  setSampleSize(Number(event.target.value) || 1)
                }
              />
            </div>

            <div className="vd-row">
              <Button
                variant="primary"
                disabled={startState.fetching}
                onClick={start}
              >
                {startState.fetching ? "試しています" : "この版で試す"}
              </Button>
            </div>
          </>
        ) : result.error !== undefined ? (
          <ErrorState
            title="試した結果を読み込めませんでした"
            description={result.error.message}
            onRetry={() => refetchResult({ requestPolicy: "network-only" })}
          />
        ) : dryRun === null ? (
          <LoadingState label="試した結果を読み込んでいます" />
        ) : (
          <>
            {pickedUp ? (
              <p className="vd-stat-note" style={{ margin: 0 }}>
                {TENANT_WIDE_JOB_NOTE}
              </p>
            ) : null}
            {isTerminal(dryRun.job.state) ? null : (
              <div className="vd-job" role="status">
                <div className="vd-card-title">
                  試しています（{count(dryRun.job.succeeded)} /{" "}
                  {count(dryRun.job.total)}）<Tag tone="info">実行中</Tag>
                </div>
                <Meter ratio={dryRun.job.progress} />
                <p className="vd-stat-note" style={{ marginBottom: 0 }}>
                  この画面を閉じても進みます。
                </p>
              </div>
            )}

            {dryRun.job.state === "FAILED" ? (
              <div className="vd-job vd-job--error" role="alert">
                <div className="vd-card-title">
                  試せませんでした
                  <Tag tone="danger">失敗</Tag>
                </div>
                <p style={{ margin: "4px 0 0" }}>
                  {dryRun.job.errorSummary ?? "理由を受け取れませんでした。"}
                </p>
              </div>
            ) : null}

            {dryRun.comparison === null ? null : (
              <ComparisonTable result={dryRun} />
            )}

            {isTerminal(dryRun.job.state) ? (
              <div className="vd-row">
                <Button
                  onClick={() => {
                    setJobId(null);
                    setPickedUp(false);
                  }}
                >
                  もう一度試す
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Card>
  );
}
